/**
 * route-checkout-subtotal-update.js
 *
 * Rewrites Route's cloned checkout button to show cart subtotal + Route
 * package protection cost as one combined total ("Checkout $138.03" for a
 * $118.00 subtotal + $20.03 quote), splicing the numeric token in place so
 * the theme's own currency format survives.
 *
 * Route mirrors the original button's raw text onto the clone on every
 * original-button change, and rebuilds the clone outright on every cart
 * update. So a guard observer restores the last combined total whenever the
 * clone is reset (before the browser paints), each rebuilt clone inherits
 * that total immediately, and reconcile() recomputes it on a short debounce
 * — Route's cost lags a subtotal change by ~100-350ms and exposes no
 * "settled" signal. lastKnownGood is therefore only ever a real
 * subtotal+cost combination, never a bare subtotal.
 *
 * The price on both the original and cloned buttons lives in a nested
 * <span class="cart-drawer__button-price">, not a bare text node — so
 * findPriceTextNode looks inside that span rather than at the button's own
 * direct children.
 *
 * Requires Route, detected via #route-widget-observer-root.
 *   <script src="{{ 'route-checkout-subtotal-update.js' | asset_url }}" defer></script>
 */
(function () {
  const OBSERVER_ROOT_SELECTOR   = '#cart-drawer';
  const CLONED_BUTTON_SELECTOR   = '[data-route-cloned-button="true"]';
  const PRICE_SPAN_SELECTOR      = '.cart-drawer__button-price';
  const ROUTE_PRICE_SELECTOR     = '.pw-coverage-description';
  const ROUTE_ROOT_ID            = 'route-widget-observer-root';
  const ROUTE_ROOT_TIMEOUT_MS    = 10000;
  const PRICE_CORRECTED_ATTR     = 'data-route-price-corrected'; // CSS hook
  const RECONCILE_SETTLE_MS      = 500;
  const MAX_RECONCILE_BAIL_RETRIES = 10;

  // The clone copies the original's classes but not its name attribute.
  const ORIGINAL_BUTTON_SELECTOR = 'button[name="checkout"]:not([data-route-cloned-button])';

  // The drawer only ever holds one clone, so this is a single slot, not a map.
  let clonedButton     = null;
  let originalButton   = null;
  let guardObserver    = null;
  let originalObserver = null;
  let reconcileTimer   = null;
  let lastKnownGood    = null;
  let bailRetries      = 0;

  let outerObserver = null;
  let routePriceObserver = null;
  let pendingScan = false;

  // Keeps lastKnownGood, so a rebuilt clone can inherit it.
  function detachCloneObservers() {
    if (guardObserver) guardObserver.disconnect();
    if (originalObserver) originalObserver.disconnect();
    clearTimeout(reconcileTimer);
    clonedButton = null;
    originalButton = null;
    guardObserver = null;
    originalObserver = null;
    reconcileTimer = null;
    bailRetries = 0;
  }

  // Drops lastKnownGood too — for when the page context itself is suspect
  // (unload, bfcache restore), not a routine clone rebuild.
  function shutdown() {
    if (outerObserver) outerObserver.disconnect();
    if (routePriceObserver) routePriceObserver.disconnect();
    detachCloneObservers();
    outerObserver = null;
    routePriceObserver = null;
    lastKnownGood = null;
  }

  // ─── Money parsing ──────────────────────────────────────────────────────────

  function extractNumericToken(text) {
    const match = text.match(/\d[\d.,]*\d|\d/);
    return match ? match[0] : null;
  }

  // Index of the token's decimal separator, or -1 if it has none: a
  // separator trailed by exactly three digits is a thousands separator, so
  // "1,234" and "1.234" both mean 1234.
  function decimalSepIndex(token) {
    const i = Math.max(token.lastIndexOf('.'), token.lastIndexOf(','));
    return i !== -1 && token.length - i - 1 === 3 ? -1 : i;
  }

  function parseNumericToken(token) {
    if (!token) return null;
    const i = decimalSepIndex(token);
    const digits = i === -1
      ? token.replace(/[.,]/g, '')
      : token.slice(0, i).replace(/[.,]/g, '') + '.' + token.slice(i + 1);
    const value = parseFloat(digits);
    return isNaN(value) ? null : value;
  }

  // Mirrors the token's own separators instead of imposing Route's Intl
  // formatting on what is a theme element.
  function formatValueMatchingToken(token, value) {
    const i = decimalSepIndex(token);
    const decimalSep = i === -1 ? (token.includes('.') ? ',' : '.') : token[i];
    const groupSep = decimalSep === '.' ? ',' : '.';
    const [intPart, fracPart] = value.toFixed(2).split('.');
    return intPart.replace(/\B(?=(\d{3})+(?!\d))/g, groupSep) + decimalSep + fracPart;
  }

  // ─── Element lookup ─────────────────────────────────────────────────────────

  // The price lives in a nested .cart-drawer__button-price span, as a bare
  // text node inside it — not directly on the button.
  function findPriceTextNode(button) {
    if (!button) return null;
    const span = button.querySelector(PRICE_SPAN_SELECTOR);
    if (!span) return null;
    for (const node of span.childNodes) {
      if (node.nodeType === Node.TEXT_NODE && extractNumericToken(node.textContent)) {
        return node;
      }
    }
    return null;
  }

  function getRouteCostValue() {
    const el = document.querySelector(ROUTE_PRICE_SELECTOR);
    if (!el) return null;
    const token = extractNumericToken(el.textContent);
    if (!token) return null;
    return parseNumericToken(token);
  }

  function getOriginalSubtotalInfo() {
    if (!originalButton) return null;
    const textNode = findPriceTextNode(originalButton);
    if (!textNode) return null;
    const text = textNode.textContent;
    const token = extractNumericToken(text);
    if (!token) return null;
    const value = parseNumericToken(token);
    if (value === null) return null;
    const tokenIndex = text.indexOf(token);
    return {
      value,
      token,
      prefix: text.slice(0, tokenIndex),
      suffix: text.slice(tokenIndex + token.length),
    };
  }

  // The selector is already drawer-scoped and the drawer holds exactly one
  // checkout button, so pairing needs no ancestor walk.
  function findPairedOriginal() {
    return document.querySelector(ORIGINAL_BUTTON_SELECTOR);
  }

  // ─── Guard ──────────────────────────────────────────────────────────────────

  // Restores lastKnownGood whenever the clone's text drifts from it. Reads
  // live DOM state rather than mutation records, so it can stay attached
  // through our own writes: commitValue sets lastKnownGood first, so the
  // callback that write triggers finds them equal and does nothing.
  function attachGuard() {
    if (guardObserver) guardObserver.disconnect();

    guardObserver = new MutationObserver(() => {
      if (lastKnownGood === null) return;
      const textNode = findPriceTextNode(clonedButton);
      if (!textNode) return;
      if (textNode.textContent !== lastKnownGood) {
        textNode.textContent = lastKnownGood;
      }
    });

    // characterData matters as much as childList: writes mutate the text
    // node's data in place rather than swapping elements.
    guardObserver.observe(clonedButton, { subtree: true, childList: true, characterData: true });
  }

  // ─── Reconcile ──────────────────────────────────────────────────────────────

  function scheduleReconcile() {
    clearTimeout(reconcileTimer);
    reconcileTimer = setTimeout(reconcile, RECONCILE_SETTLE_MS);
  }

  // A bail leaves lastKnownGood untouched, so retrying only decides how soon
  // the first combination lands — capped in case it never can.
  function retryAfterBail() {
    bailRetries += 1;
    if (bailRetries > MAX_RECONCILE_BAIL_RETRIES) return;
    scheduleReconcile();
  }

  // LOCAL DEVIATION from Route's 09/16/2026 file — do not drop on the next
  // vendor sync. The button's label ("Checkout") is its text outside the
  // price span; the mini cart nests both inside wrapper spans, so read the
  // whole text minus the price span rather than only direct text nodes. The
  // accessible name has to carry both.
  function getButtonLabelText(button) {
    const copy = button.cloneNode(true);
    copy.querySelectorAll(PRICE_SPAN_SELECTOR).forEach((span) => span.remove());
    return copy.textContent.replace(/\s+/g, ' ').trim();
  }

  // The only writer of lastKnownGood.
  function commitValue(textNode, newText) {
    lastKnownGood = newText; // before the write, so the guard no-ops on it
    textNode.textContent = newText;
    clonedButton.setAttribute(PRICE_CORRECTED_ATTR, '');

    // Route sets its own aria-label once, at clone-creation time, from
    // whatever the raw subtotal was at that instant — it's never a
    // combined total, so unlike the guarded price text there's nothing of
    // Route's worth preserving here. Always keep it in sync with what's
    // displayed.
    //
    // LOCAL DEVIATION: Route's version set the label to `newText` alone, which
    // is only the price span's text — that renamed the button from
    // "Checkout $ 142.95" to "$ 142.95", dropping the visible label from the
    // accessible name (WCAG 2.5.3 Label in Name). Prepend the button's own
    // text so the accessible name still contains what the shopper sees.
    const price = newText.replace(/\s+/g, ' ').trim();
    const label = getButtonLabelText(clonedButton);
    clonedButton.setAttribute('aria-label', label ? `${label} ${price}` : price);
  }

  function reconcile() {
    // No clone means nothing to correct, and processClonedButton schedules a
    // reconcile once one appears — so this exit needs no retry.
    if (!clonedButton) return;

    // LOCAL DEVIATION from Route's 09/16/2026 file — do not drop on the next
    // vendor sync. Route returned bare here. A clone whose price span hasn't
    // rendered yet is transient (they rebuild the clone wholesale), but nothing
    // reschedules: scanForClonedButtons early-returns while the clone is still
    // connected, and the guard only restores lastKnownGood. Without a retry the
    // button keeps a bare subtotal until the original button happens to mutate.
    const textNode = findPriceTextNode(clonedButton);
    if (!textNode) return retryAfterBail();

    const routeCost = getRouteCostValue();
    if (routeCost === null) return retryAfterBail();

    const originalInfo = getOriginalSubtotalInfo();
    if (!originalInfo) return retryAfterBail();
    bailRetries = 0;

    const formattedNumber = formatValueMatchingToken(originalInfo.token, originalInfo.value + routeCost);
    const newText = originalInfo.prefix + formattedNumber + originalInfo.suffix;

    if (newText === lastKnownGood && textNode.textContent === newText) return;

    commitValue(textNode, newText);
  }

  // Skips the debounce. Only safe for a trigger that fires after its value
  // already landed in the DOM, which a MutationObserver callback always
  // does — watchRoutePrice is the sole such caller. Triggers that fire when
  // a node merely appeared keep the debounce, since a fresh node's content
  // may still be a placeholder.
  function reconcileNow() {
    clearTimeout(reconcileTimer);
    reconcile();
  }

  // ─── Watchers ───────────────────────────────────────────────────────────────

  function watchOriginalSubtotal() {
    if (!originalButton) return;
    if (originalObserver) originalObserver.disconnect();

    // No write here: Route's own sync resets the clone in reaction to this
    // same mutation and the guard restores lastKnownGood before paint. The
    // reconcile is what swaps in the new combination once Route's cost
    // catches up to the new subtotal.
    originalObserver = new MutationObserver(scheduleReconcile);
    originalObserver.observe(originalButton, { characterData: true, childList: true, subtree: true });
  }

  function watchRoutePrice() {
    if (routePriceObserver) routePriceObserver.disconnect();
    const routePriceEl = document.querySelector(ROUTE_PRICE_SELECTOR);
    if (!routePriceEl) return;

    routePriceObserver = new MutationObserver(reconcileNow);
    routePriceObserver.observe(routePriceEl, { characterData: true, childList: true, subtree: true });
  }

  // Route replaces some of its elements wholesale rather than mutating them,
  // which silently kills an observer bound to the old node. Each tracker
  // reports whether its element's node changed since the last call.
  function elementTracker(selector) {
    let current = null;
    return () => {
      // A node still in the document cannot have been replaced, so the steady
      // state costs a property read instead of a DOM query — this runs on
      // every childList mutation on the page.
      if (current && current.isConnected) return { changed: false, current };

      const latest = document.querySelector(selector);
      const changed = latest !== current;
      current = latest;
      return { changed, current: latest };
    };
  }

  const syncDrawerRoot = elementTracker(OBSERVER_ROOT_SELECTOR);
  const syncRoutePrice = elementTracker(ROUTE_PRICE_SELECTOR);

  // ─── Clone lifecycle ────────────────────────────────────────────────────────

  function processClonedButton(btn) {
    if (btn === clonedButton) return;

    if (clonedButton) detachCloneObservers();

    clonedButton = btn;
    originalButton = findPairedOriginal();
    attachGuard();
    watchOriginalSubtotal();

    // A rebuilt clone shows Route's raw subtotal, so overwrite it with the
    // previous combined total at once. That total may be a beat stale, but a
    // stale combination beats a fresh bare subtotal, and the reconcile below
    // corrects it. Only the page's first clone has nothing to inherit.
    const textNode = findPriceTextNode(btn);
    if (lastKnownGood !== null && textNode && textNode.textContent !== lastKnownGood) {
      commitValue(textNode, lastKnownGood);
    }

    scheduleReconcile();
  }

  function scanForClonedButtons() {
    if (clonedButton && clonedButton.isConnected) return; // still the live clone

    const btn = document.querySelector(CLONED_BUTTON_SELECTOR);
    if (btn) {
      if (btn !== clonedButton) processClonedButton(btn);
    } else if (clonedButton) {
      detachCloneObservers();
    }
  }

  function attachObserver() {
    if (outerObserver) outerObserver.disconnect();
    if (!syncDrawerRoot().current) return;
    syncRoutePrice();
    watchRoutePrice();

    outerObserver = new MutationObserver((mutationList) => {
      // Type-only check, no per-node queries: Route's re-render can emit a
      // huge batch, and querying per node here once froze the page.
      let sawChildList = false;
      for (const mutation of mutationList) {
        if (mutation.type === 'childList') {
          sawChildList = true;
          break;
        }
      }
      if (!sawChildList) return;

      if (pendingScan) return;
      pendingScan = true;
      queueMicrotask(() => {
        pendingScan = false;

        if (syncDrawerRoot().changed && clonedButton) detachCloneObservers();

        const price = syncRoutePrice();
        if (price.changed) {
          // Called even when the element is gone: it disconnects first, so a
          // vanished price element does not leave an observer on a detached node.
          watchRoutePrice();
          if (price.current) scheduleReconcile();
        }

        scanForClonedButtons();
      });
    });

    // Watched from body, not #cart-drawer: if the theme swaps that element
    // out wholesale, an observer bound to the old node goes silent forever.
    outerObserver.observe(document.body, { childList: true, subtree: true });
    scanForClonedButtons();
  }

  // ─── Init ───────────────────────────────────────────────────────────────────

  // Only the Route gate is on a timer — no Route root within
  // ROUTE_ROOT_TIMEOUT_MS means Route is not on this page. Once it appears the
  // timer is dropped and the cart root is waited on indefinitely, since the
  // theme can render it as late as the shopper's first drawer open.
  function waitForRoot() {
    let routeRootSeen = !!document.getElementById(ROUTE_ROOT_ID);
    if (routeRootSeen && document.querySelector(OBSERVER_ROOT_SELECTOR)) {
      attachObserver();
      return;
    }

    let routeRootTimeout = null;
    const bodyObserver = new MutationObserver(() => {
      if (!routeRootSeen) {
        if (!document.getElementById(ROUTE_ROOT_ID)) return;
        routeRootSeen = true;
        clearTimeout(routeRootTimeout);
      }
      if (!document.querySelector(OBSERVER_ROOT_SELECTOR)) return;
      bodyObserver.disconnect();
      attachObserver();
    });

    bodyObserver.observe(document.body, { childList: true, subtree: true });

    if (!routeRootSeen) {
      routeRootTimeout = setTimeout(() => bodyObserver.disconnect(), ROUTE_ROOT_TIMEOUT_MS);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', waitForRoot);
  } else {
    waitForRoot();
  }

  window.addEventListener('pagehide', shutdown);

  window.addEventListener('pageshow', (e) => {
    if (!e.persisted) return;
    shutdown(); // clears observers bound to pre-freeze nodes before rebinding
    attachObserver();
  });
})();
